import { Entity } from '@inter-ikea-core/core';
import {
  InnerLineCenterPoint2D,
  DistanceMeasurementComponent,
  DistanceLineCenterComponent,
  LabelPosition,
  DistanceMeasurementSystem
} from '@inter-ikea-storageone/range';
import MeasurementsLabel, { getLabel } from 'components/MeasurementsLabel/MeasurementsLabel';
import { useCore } from 'contexts/core';
import { useServiceSettings } from 'contexts/serviceSettings';
import { isMeasurementsVisible } from 'ducks/layout';
import { getSelectedProduct } from 'ducks/products';
import { useTranslations } from 'hooks/useTranslations';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import theme from 'ui/theme';

const DistanceMeasurementWidget = () => {
  const [labels, setLabels] = useState<LabelPosition[]>([]);
  const coreApi = useCore();
  const selectedProduct = useSelector(getSelectedProduct);
  const selectedEntity = selectedProduct ? coreApi.entities.find(selectedProduct.entityId) : null;
  const { useMetric } = useTranslations();
  const { translations } = useServiceSettings();
  const measurementsVisible = useSelector(isMeasurementsVisible);

  useEffect(() => {
    const updateLabels = (measurements: InnerLineCenterPoint2D[]) => {
      if (!measurementsVisible) {
        return;
      }
      const labels = measurements.map((measurement, index) =>
        getLabel(index.toString(), measurement.distance, measurement, translations, useMetric)
      );
      setLabels(labels);
    };

    const entityManager = coreApi.getServices().entityManager;

    entityManager.subscribeAdd(
      DistanceLineCenterComponent.id,
      (entity: Entity, newComponent: DistanceLineCenterComponent) => {
        updateLabels(newComponent.measurements);
      }
    );

    entityManager.subscribeUpdate(
      DistanceLineCenterComponent.id,
      (entity: Entity, newComponent: DistanceLineCenterComponent) => {
        updateLabels(newComponent.measurements);
      }
    );

    entityManager.subscribeRemove(DistanceLineCenterComponent.id, () => {
      updateLabels([]);
    });
  }, [coreApi, measurementsVisible, useMetric, translations]);

  useEffect(() => {
    if (!selectedEntity || !selectedEntity.hasComponent(DistanceMeasurementComponent.id)) {
      setLabels([]);
    }
  }, [selectedEntity]);

  useEffect(() => {
    const distanceMeasurementSystem = coreApi.getSystem<DistanceMeasurementSystem>(
      'DistanceMeasurementSystem'
    );
    if (measurementsVisible) {
      distanceMeasurementSystem?.enable();
    } else {
      distanceMeasurementSystem?.disable();
      setLabels([]);
    }
  }, [coreApi, measurementsVisible]);

  return (
    <>
      {labels.map(
        ({ dim, x, y, size }) =>
          parseInt(size) > 0 && (
            <MeasurementsLabel
              key={dim}
              dim={dim}
              x={x}
              y={y}
              size={size}
              visible={true}
              bgColor={theme.colors.feedback.selected}
            />
          )
      )}
    </>
  );
};

export default DistanceMeasurementWidget;
